import { useState, useEffect } from 'react';
import { activityApi, Activity, ActivityFilters } from '@/lib/activityApi';
import { workspaceMemberApi, WorkspaceMember } from '@/lib/workspaceMemberApi';
import { Button, Badge, Select, SelectItem } from '@fyos/components';
import { X, ChevronLeft, ChevronRight, Activity as ActivityIcon } from 'lucide-react';
import type { PaginationMeta } from '@/lib/api';

interface ActivityModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}

export default function ActivityModal({ open, onClose, workspaceId }: ActivityModalProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);

  const [filterType, setFilterType] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');

  useEffect(() => {
    if (open && workspaceId) {
      workspaceMemberApi.getAll(workspaceId).then(setMembers).catch(() => {});
    }
  }, [open, workspaceId]);

  useEffect(() => {
    if (open && workspaceId) {
      fetchActivities();
    }
  }, [open, workspaceId, page, filterType, filterAction, filterUser]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const filters: ActivityFilters = {};
      if (filterType) filters.subject_type = filterType;
      if (filterAction) filters.action = filterAction;
      if (filterUser) filters.user_id = filterUser;
      const result = await activityApi.getPaginated(workspaceId, page, 30, filters);
      setActivities(result?.data ?? []);
      setMeta(result?.meta ?? null);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilterType('');
    setFilterAction('');
    setFilterUser('');
    setPage(1);
  };

  const getActionColor = (action: string): string => {
    if (action === 'created' || action === 'published') return 'success';
    if (action === 'deleted' || action === 'unpublished' || action === 'member_removed') return 'error';
    if (action.startsWith('bulk') || action === 'reordered' || action === 'role_changed') return 'info';
    if (action === 'moved') return 'warning';
    return 'gray';
  };

  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      item: 'Item',
      comment: 'Comment',
      changelog: 'Changelog',
      workspace: 'Workspace',
      column: 'Column',
      category: 'Category',
      tag: 'Tag',
      attachment: 'Attachment',
      custom_field: 'Custom Field',
      webhook: 'Webhook',
      api_key: 'API Key',
    };
    return labels[type] || type;
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const hasActiveFilters = filterType || filterAction || filterUser;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-50 w-full max-w-2xl bg-white dark:bg-gray-800 shadow-xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <ActivityIcon className="h-5 w-5" />
            Activity
          </h2>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-1.5 px-3 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="w-36">
            <Select value={filterType || '_all'} onValueChange={(v) => { setFilterType(v === '_all' ? '' : v); setPage(1); }}>
              <SelectItem value="_all">All types</SelectItem>
              <SelectItem value="item">Item</SelectItem>
              <SelectItem value="comment">Comment</SelectItem>
              <SelectItem value="changelog">Changelog</SelectItem>
              <SelectItem value="workspace">Workspace</SelectItem>
              <SelectItem value="column">Column</SelectItem>
              <SelectItem value="category">Category</SelectItem>
              <SelectItem value="tag">Tag</SelectItem>
              <SelectItem value="attachment">Attachment</SelectItem>
              <SelectItem value="custom_field">Custom Field</SelectItem>
              <SelectItem value="webhook">Webhook</SelectItem>
              <SelectItem value="api_key">API Key</SelectItem>
            </Select>
          </div>
          <div className="w-36">
            <Select value={filterAction || '_all'} onValueChange={(v) => { setFilterAction(v === '_all' ? '' : v); setPage(1); }}>
              <SelectItem value="_all">All actions</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="updated">Updated</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
              <SelectItem value="moved">Moved</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="unpublished">Unpublished</SelectItem>
              <SelectItem value="reordered">Reordered</SelectItem>
              <SelectItem value="member_invited">Invited</SelectItem>
              <SelectItem value="member_removed">Removed</SelectItem>
              <SelectItem value="role_changed">Role Changed</SelectItem>
            </Select>
          </div>
          <div className="w-36">
            <Select value={filterUser || '_all'} onValueChange={(v) => { setFilterUser(v === '_all' ? '' : v); setPage(1); }}>
              <SelectItem value="_all">All members</SelectItem>
              {members.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
              ))}
            </Select>
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="shrink-0">
              Clear
            </Button>
          )}
        </div>

        {/* Activity list */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-14 rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse" />
              ))}
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <ActivityIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">No activity yet</p>
              <p className="text-sm mt-1">Actions in this workspace will appear here.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="h-7 w-7 shrink-0 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center text-xs font-medium text-brand-700 dark:text-brand-300 mt-0.5">
                    {activity.user?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {activity.user?.name || 'Unknown'}
                      </span>
                      <Badge variant={getActionColor(activity.action) as any}>
                        {activity.action.replace(/_/g, ' ')}
                      </Badge>
                      <Badge variant="gray">
                        {getTypeLabel(activity.subject_type)}
                      </Badge>
                    </div>
                    {activity.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 truncate">
                        {activity.description}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 shrink-0 mt-1">
                    {formatTime(activity.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {meta && meta.last_page > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700 shrink-0">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Page {meta.current_page} of {meta.last_page} ({meta.total} total)
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={meta.current_page <= 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={meta.current_page >= meta.last_page}
                onClick={() => setPage(page + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
